import { useCallback, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useAgentName } from '../../../lib/use-agent-name';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useMyAgents, type AgentSummary, type MyAgentsState, type SkillSummary } from '../../../lib/my-agents';
import { useAgentWorkspaceTasks, type AgentWorkspaceTasksState } from '../../../lib/agent-workspace-tasks';
import { useDeselectOnOutsidePress } from '../../../lib/agent-deselect';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useTaskSidePanel } from '../task-side-panel/TaskSidePanelContext';
import { SkillsModal } from './SkillsModal';

// Everything Your Agents does, independent of how it is laid out — so the desktop and
// the mobile screens are two presentations of ONE behaviour rather than two implementations
// of it. The reads (the team + the per-workspace task rollup, and their load BI), the
// shared genome/roster selection, and the two team-level actions all live here; each
// screen only decides what it draws.

export const YOUR_AGENTS_PAGE = 'your_agents';

export interface YourAgents {
  team: MyAgentsState;
  tasks: AgentWorkspaceTasksState;
  /**
   * Either primary read is still in flight, so the screen has nothing true to draw yet.
   *
   * Both are waited on deliberately. The team read alone would render "Your Team · 0" over an
   * empty figure — a confident statement that happens to be false — and letting the task strip
   * arrive separately would shove the whole page down as it lands. Waiting also means the
   * genome's arrival reel plays over the REAL roster rather than an empty one.
   *
   * A read that FAILED is not loading: the task strip simply stays out (its own absence is
   * honest), and a failed team read is the screen's error state.
   */
  loading: boolean;
  agents: AgentSummary[];
  skills: SkillSummary[];
  maestro: ReturnType<typeof useAgentName>;
  /** `null` = nothing picked: the Maestro rests at the front and the genome is undimmed. */
  selectedId: string | null;
  /** Pressing the current selection again clears it, back to that resting state (the wide pile). */
  select: (id: string) => void;
  /** Set the selection outright, no toggle — the narrow carousel drives it from the leading card,
   *  where a toggle would fight the continuous scroll sync. */
  setSelected: (id: string | null) => void;
  openSkills: () => void;
  addAgent: () => void;
}

export function useYourAgents(): YourAgents {
  const { t } = useTranslation();
  const maestro = useAgentName();
  const openSideChat = useOpenSideChat();
  const { openModal, closeModal } = useTaskSidePanel();

  const team = useMyAgents();
  const tasks = useAgentWorkspaceTasks();
  useLoadBi('agents-list-data', { isLoading: team.status === 'loading', isError: team.status === 'error' });
  useLoadBi('agents-workspace-tasks', { isLoading: tasks.status === 'loading', isError: tasks.status === 'error' });

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const agents = team.status === 'ready' ? team.team.agents : [];
  const skills = team.status === 'ready' ? team.team.skills : [];

  const openSkills = useCallback(() => {
    openModal(<SkillsModal skills={skills} />);
  }, [openModal, skills]);

  const addAgent = useCallback(() => {
    closeModal();
    openSideChat({ kind: 'new', message: t('agents.addAgentSeed') });
  }, [closeModal, openSideChat, t]);

  const select = useCallback((id: string) => {
    setSelectedId((current) => (current === id ? null : id));
  }, []);

  const setSelected = useCallback((id: string | null) => setSelectedId(id), []);

  // Pressing anywhere off the genome nodes and the agent cards returns to rest — the Maestro at
  // the front, the genome undimmed. Behaviour-only and layout-independent, so it lives here rather
  // than in either screen; both the wide pile and the narrow carousel inherit it.
  const deselect = useCallback(() => setSelectedId(null), []);
  useDeselectOnOutsidePress(selectedId !== null, deselect);

  const loading = team.status === 'loading' || tasks.status === 'loading';

  return { team, tasks, loading, agents, skills, maestro, selectedId, select, setSelected, openSkills, addAgent };
}
